import React, { useState, useEffect, useCallback } from 'react';
import { usePusherChannel, usePusherEvent, usePusherConnection } from '../hooks/usePusher';
import { sendDebateMessage, getDebateChannelName } from '../services/pusherService';
import { useAuth } from '../contexts/AuthContext';
import { useDebate } from '../contexts/DebateContext';
import { toast } from 'react-hot-toast';
import logger from '../utils/logger';

const PusherDebateChat = ({ debateId, teamId }) => {
  const [, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [, setIsLoading] = useState(false);
  const { user } = useAuth();
  const isPusherConnected = usePusherConnection();
  
  // Access DebateContext to update global state
  const { currentDebate, fetchDebateById, setCurrentTeamWithFlag, handleAIAnalysisComplete, startFallbackPolling } = useDebate();
  
  // Get a Pusher channel for this debate
  const channelName = getDebateChannelName(debateId);
  const debateChannel = usePusherChannel(channelName);
  
  // Log connection status changes - FIXED to prevent infinite loops
  useEffect(() => {
    logger.info('🔌 PUSHER CHAT: Connection status:', { 
      isConnected: isPusherConnected,
      debateId,
      teamId,
      channelName,
      hasDebateChannel: !!debateChannel
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- omit debateChannel to prevent loops
  }, [isPusherConnected, debateId, teamId, channelName]);
  
  // Handle new messages coming in via Pusher
  const handleNewMessage = useCallback((data) => {
    logger.info('🔔 PUSHER CHAT: Received message_received event:', {
      dataId: data.id || data._id,
      dataContent: data.content?.substring(0, 30) + (data.content?.length > 30 ? '...' : ''),
      dataTeamId: data.teamId,
      dataSenderId: data.senderId,
      dataDebateId: data.debateId,
      currentDebateId: currentDebate?.id,
      isForCurrentDebate: currentDebate && currentDebate.id === data.debateId
    });
    
    // Only update if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      logger.info('🔔 PUSHER CHAT: Adding new message to current debate from Pusher event');
      
      const newMessage = {
        id: data.id,
        _id: data.id,
        content: data.content,
        senderId: data.senderId,
        teamId: data.teamId,
        flags: data.flags || [],
        timestamp: data.timestamp,
        createdAt: data.timestamp,
        isReformulation: data.isReformulation || false,
        isProcessing: data.isProcessing || false
      };
      
      // Update local state
      setMessages(prev => [...prev, newMessage]);
      
      // Skip refresh for any message that's still processing to preserve processing state
      if (data.isProcessing) {
        logger.info('🔔 PUSHER CHAT: Skipping refresh for processing message to preserve processing state', {
          messageId: data.id,
          senderId: data.senderId,
          isOwnMessage: data.senderId === user?.id
        });
        
        // HYBRID: Wait for Pusher event first, then fallback polling after 15s
        if (data.senderId !== user?.id && data.id) {
          logger.info('🔔 PUSHER CHAT: Received processing message from other user - waiting for analysis_complete event', {
            messageId: data.id,
            debateId: data.debateId
          });
          
          // Fallback: Start polling after 15 seconds if no Pusher event (serverless timeout protection)
          const timeoutId = setTimeout(() => {
            logger.info('🔔 PUSHER CHAT: No Pusher event received within 15s, starting fallback polling');
            startFallbackPolling(data.debateId, data.id);
          }, 15000); // 15 second timeout
          
          // Store timeout ID to cancel if Pusher event arrives
          window.analysisTimeouts = window.analysisTimeouts || new Map();
          window.analysisTimeouts.set(data.id, timeoutId);
        }
      } else {
        // Only refresh for completed messages from other users
        if (data.senderId !== user?.id) {
          try {
            logger.info('🔔 PUSHER CHAT: Refreshing for completed message from other user');
            if (fetchDebateById && currentDebate?.id) {
              setTimeout(() => {
                fetchDebateById(currentDebate.id, true)
                  .then(() => logger.info('🔔 PUSHER CHAT: Successfully refreshed debate data'))
                  .catch(err => logger.error('🔔 PUSHER CHAT: Error refreshing debate data:', err));
              }, 200);
            }
          } catch (error) {
            logger.error('🔔 PUSHER CHAT: Error refreshing debate data:', error);
          }
        } else {
          logger.info('🔔 PUSHER CHAT: Skipping refresh for own completed message');
        }
      }
      
      // Show toast notification - DISABLE to prevent duplicate toasts
      // Toast notifications are now handled by the main DebateContext only
      // if (data.flags && data.flags.length > 0) {
      //   // Only show for other people's messages to avoid duplicate notifications
      //   if (data.senderId !== user?.id) {
      //     toast.error('A message with flags was received!');
      //   }
      // } else {
      //   // Only show for other people's messages to avoid duplicate notifications
      //   if (data.senderId !== user?.id) {
      //     toast.success('New message received!');
      //   }
      // }
    } else {
      logger.info('🔔 PUSHER CHAT: Message is not for current debate, ignoring', {
        currentDebateId: currentDebate?.id,
        messageDebateId: data.debateId
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchDebateById/startFallbackPolling stable
  }, [currentDebate, user?.id]);
  
  // Also add direct binding to the channel when it becomes available
  useEffect(() => {
    if (!debateChannel) return;
    
    logger.info('🔌 PUSHER CHAT: Setting up direct binding to message_received event');
    
    // Add direct binding to ensure we catch all messages
    const messageHandler = (data) => {
      logger.info('🔔 PUSHER CHAT: Direct binding caught message_received event');
      handleNewMessage(data);
    };
    
    debateChannel.bind('message_received', messageHandler);
    
    // Cleanup when unmounting
    return () => {
      debateChannel.unbind('message_received', messageHandler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleNewMessage would change every render
  }, [debateChannel]);
  
  // Handle shitmove detection events
  const handleShitmoveDetected = useCallback((data) => {
    logger.info('🚨 PUSHER EVENT: shitmove_detected received:', {
      messageId: data.messageId,
      debateId: data.debateId,
      flagCount: data.flags?.length || 0,
      isProcessing: data.isProcessing
    });
    
    // Only update if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      logger.info('PusherDebateChat: Updating message flags from Pusher event');
      

      
      // If this was a reformulated message that got flagged again, we need to reset the reformulation state
      // We have to check this before refresh, but we'll make this lightweight
      if (typeof setCurrentTeamWithFlag === 'function') {
        setCurrentTeamWithFlag(null); // Reset flag anyway to be safe
      }
      
      // CRITICAL UPDATE: Force fetchDebateById to get the latest flags
      try {
        logger.info('🔔 PUSHER CHAT: Received flag update - forcing fetch of latest debate data');
        if (fetchDebateById && currentDebate?.id) {
          // Reduce timeout to prevent excessive fetching and race conditions
          setTimeout(() => {
            fetchDebateById(currentDebate.id, true)
              .then(() => {
                logger.info('🔔 PUSHER CHAT: Successfully refreshed debate data with flags');
                
                // Use the centralized function to handle AI analysis completion
                if (typeof handleAIAnalysisComplete === 'function') {
                  handleAIAnalysisComplete(data.messageId, true, data.senderId); // true = has flags
                }
              })
              .catch(err => logger.error('🔔 PUSHER CHAT: Error refreshing debate data with flags:', err));
          }, 200); // Increased from 100ms to 200ms to reduce frequency
        } else {
          logger.info('🔔 PUSHER CHAT: Cannot fetch latest debate data with flags - missing fetchDebateById or currentDebate.id');
        }
      } catch (error) {
        logger.error('🔔 PUSHER CHAT: Error refreshing debate data with flags:', error);
      }

      // Show toast notification for shitmove
      toast.error('A logical fallacy was detected in a message!');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchDebateById/handleAIAnalysisComplete stable
  }, [currentDebate, setCurrentTeamWithFlag]);
  
  // Handle analysis complete events (when AI analysis finishes with no flags)
  const handleAnalysisComplete = useCallback((data) => {
    logger.info('✅ PUSHER EVENT: analysis_complete received:', {
      messageId: data.messageId,
      debateId: data.debateId,
      hasFlags: data.hasFlags,
      isProcessing: data.isProcessing
    });
    
    // Cancel fallback polling timeout if Pusher event arrives
    const timeoutId = window.analysisTimeouts?.get(data.messageId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      window.analysisTimeouts.delete(data.messageId);
      logger.info('🔔 PUSHER CHAT: Cancelled fallback polling - Pusher event received');
    }
    
    // Only update if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      logger.info('PusherDebateChat: AI analysis completed');
      
      // Use the centralized function to handle AI analysis completion
      if (typeof handleAIAnalysisComplete === 'function') {
        handleAIAnalysisComplete(data.messageId, data.hasFlags, data.senderId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchDebateById/handleAIAnalysisComplete stable
  }, [currentDebate, handleAIAnalysisComplete]);
  
  // Handle flag lifted events
  const handleFlagLifted = useCallback((data) => {
    logger.info('Received flag_lifted event via Pusher:', data);
    
    // Only update if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      logger.info('PusherDebateChat: Updating message flag from Pusher event');
      
      // CRITICAL UPDATE: Add debouncing to prevent excessive fetching
      const now = Date.now();
      const lastFlagFetch = window.lastPusherFlagFetch || 0;
      
      if (now - lastFlagFetch > 2000) { // Only fetch if last fetch was more than 2 seconds ago
        window.lastPusherFlagFetch = now;
        
      try {
        logger.info('🔔 PUSHER CHAT: Received flag lifted update - forcing fetch of latest debate data');
        if (fetchDebateById && currentDebate?.id) {
          // Reduce timeout to prevent excessive fetching
          setTimeout(() => {
            fetchDebateById(currentDebate.id, true)
              .then(() => logger.info('🔔 PUSHER CHAT: Successfully refreshed debate data with lifted flag'))
              .catch(err => logger.error('🔔 PUSHER CHAT: Error refreshing debate data with lifted flag:', err));
            }, 500); // Increased from 200ms to 500ms to reduce frequency
        } else {
          logger.info('🔔 PUSHER CHAT: Cannot fetch latest debate data with lifted flag - missing fetchDebateById or currentDebate.id');
        }
      } catch (error) {
        logger.error('🔔 PUSHER CHAT: Error refreshing debate data with lifted flag:', error);
        }
      } else {
        logger.info('🔔 PUSHER CHAT: Skipping flag fetch - too soon since last flag fetch');
      }
      
      // Show success toast notification when a flag is lifted - DISABLE to prevent duplicate toasts
      // toast.success('A flag has been lifted!');
    }
  }, [currentDebate, fetchDebateById]);
  
  // Handle reformulation request events
  const handleReformulationRequest = useCallback((data) => {
    logger.info('Received reformulation_request event via Pusher:', data);
    
    // Only update if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      logger.info('PusherDebateChat: Setting currentTeamWithFlag from Pusher event:', data.teamId);
      
      // Set the current team with flag
      if (typeof setCurrentTeamWithFlag === 'function') {
        setCurrentTeamWithFlag(data.teamId);
      }
      
      // Note: Reformulation request state is now handled by the main DebateContext
      // No need to update local state here as it will be refreshed via fetchDebateById
      
      // Show notification based on self-initiated status and if it's for the current team
      if (data.teamId === teamId) {
        if (data.selfInitiated) {
          // Self-initiated reformulation
          toast.success('You have chosen to reformulate your message');
        } else {
          // Reformulation requested by the other team
          toast.error('The other team has requested that you reformulate your message');
        }
      } else {
        // Show notification to the other team that a team has chosen to reformulate
        if (data.selfInitiated) {
          toast.success('The other team has chosen to reformulate their message');
        }
      }
    }
  }, [currentDebate, setCurrentTeamWithFlag, teamId]);
  
  // Handle reformulation reset events
  const handleReformulationReset = useCallback((data) => {
    logger.info('Received reformulation_reset event via Pusher:', data);
    
    // Only reset if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      logger.info('PusherDebateChat: Resetting currentTeamWithFlag for this debate');
      
      // Reset the current team with flag
      if (typeof setCurrentTeamWithFlag === 'function') {
        setCurrentTeamWithFlag(null);
      }
      
      // Note: Reformulation reset state is now handled by the main DebateContext
      // No need to update local state here as it will be refreshed via fetchDebateById
    }
  }, [currentDebate, setCurrentTeamWithFlag]);
  
  // Handle debate updates
  const handleDebateUpdated = useCallback((data) => {
    logger.info('Received debate_updated event via Pusher:', data);
    
    // If we're viewing this debate, refresh it
    if (currentDebate && data.id === currentDebate.id) {
      logger.info('PusherDebateChat: Refreshing debate data from server');
      
      // Add debouncing to prevent excessive fetching
      const now = Date.now();
      const lastDebateUpdateFetch = window.lastPusherDebateUpdateFetch || 0;
      
      if (now - lastDebateUpdateFetch > 3000) { // Only fetch if last fetch was more than 3 seconds ago
        window.lastPusherDebateUpdateFetch = now;
      
      // Use the fetchDebateById function to get fresh data
      if (typeof fetchDebateById === 'function') {
        fetchDebateById(data.id, true).catch(err => {
          logger.error('Error refreshing debate after debate_updated event:', err);
        });
        }
      } else {
        logger.info('PusherDebateChat: Skipping debate update fetch - too soon since last update fetch');
      }
      
      // Show a notification about the update if there's a specific type
      if (data.updateType) {
        switch (data.updateType) {
          case 'status_change':
            if (data.newStatus === 'completed') {
              toast.success('The debate has been completed!');
            } else {
              toast.info(`Debate status changed to: ${data.newStatus}`);
            }
            break;
          case 'winner_set':
            toast.success(`A winner has been declared: ${data.winnerName || 'See debate results'}`);
            break;
          default:
            toast.info('The debate has been updated');
        }
      }
    }
  }, [currentDebate, fetchDebateById]);
  
  // Bind to all Pusher events
  usePusherEvent(debateChannel, 'message_received', handleNewMessage);
  usePusherEvent(debateChannel, 'shitmove_detected', handleShitmoveDetected);
  usePusherEvent(debateChannel, 'analysis_complete', handleAnalysisComplete);
  usePusherEvent(debateChannel, 'flag_lifted', handleFlagLifted);
  usePusherEvent(debateChannel, 'reformulation_request', handleReformulationRequest);
  usePusherEvent(debateChannel, 'reformulation_reset', handleReformulationReset);
  usePusherEvent(debateChannel, 'debate_updated', handleDebateUpdated);
  usePusherEvent(debateChannel, 'analysis_complete', handleAnalysisComplete);
  
  // Include debug event to log all events
  usePusherEvent(debateChannel, '*', (data) => {
    logger.info('🔔 PUSHER CHAT DEBUG: Catch-all event handler triggered:', {
      eventName: data?.event || 'unknown',
      channelName,
      data: typeof data === 'object' ? {
        ...data,
        content: data.content?.substring(0, 30) + (data.content?.length > 30 ? '...' : '')
      } : data
    });
  });
  
  // Send a new message (reserved for future UI)
  // eslint-disable-next-line no-unused-vars
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !user?.id) return;
    
    setIsLoading(true);
    
    try {
      const result = await sendDebateMessage(
        debateId,
        newMessage,
        teamId,
        user.id
      );
      
      logger.info('Message sent:', result);
      setNewMessage('');
    } catch (error) {
      logger.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };
  
  // This component is now hidden in the UI, so we don't need to render much
  return (
    <div style={{ display: 'none' }}>
      <div id="pusher-debate-chat-container"></div>
    </div>
  );
};

export default PusherDebateChat; 